import React from 'react';
import type { CSSProperties } from 'react';
import { FormLayout, Input } from '../../components/ui';
import { brand } from '../../theme/tokens';
import { THEME_CHOICES, UI_SCALES, NAV_LAYOUTS, DEFAULT_NAV_LAYOUT } from '../../utils/churchSettings';
import type { NavLayout } from '../../utils/churchSettings';
import type { Tokens } from '../../theme/tokens';
import type { SettingsSectionProps } from './SettingsPage';

// How the app looks for everyone: the way around it (tiles, sidebar or top
// menu), the brand colour, the colour scheme a new browser opens in, and the
// display size.

/**
 * Starting points for the brand colour. Any hex is accepted — these are just
 * the ones that sit well with the app's surfaces and stay legible in both
 * light and dark mode.
 */
const BRAND_PRESETS = [
  { value: '',        label: 'Liturgical Purple', swatch: brand.primary },
  { value: '#1565C0', label: 'Sapphire',          swatch: '#1565C0' },
  { value: '#00695C', label: 'Deep Teal',         swatch: '#00695C' },
  { value: '#2E7D32', label: 'Forest',            swatch: '#2E7D32' },
  { value: '#AD1457', label: 'Claret',            swatch: '#AD1457' },
  { value: '#B34700', label: 'Amber',             swatch: '#B34700' },
  { value: '#37474F', label: 'Slate',             swatch: '#37474F' }
];

/**
 * A plain-English name for any hex colour — the nearest of a small palette —
 * so the office sees "Deep purple" beside "#6B4277" rather than only a code
 * (CHM-090). Presets keep their own names.
 */
const NAMED_COLOURS: Array<[string, string]> = [
  ['Deep purple', '#6B4277'], ['Purple', '#7B1FA2'], ['Violet', '#5E35B1'], ['Indigo', '#3949AB'],
  ['Navy blue', '#1A237E'], ['Blue', '#1565C0'], ['Sky blue', '#0288D1'], ['Teal', '#00695C'],
  ['Cyan', '#00838F'], ['Green', '#2E7D32'], ['Light green', '#558B2F'], ['Olive', '#827717'],
  ['Yellow', '#F9A825'], ['Orange', '#EF6C00'], ['Amber brown', '#B34700'], ['Brown', '#5D4037'],
  ['Red', '#C62828'], ['Maroon', '#880E4F'], ['Claret', '#AD1457'], ['Pink', '#D81B60'],
  ['Slate grey', '#37474F'], ['Grey', '#616161'], ['Black', '#212121'], ['White', '#FAFAFA']
];
const colourName = (hex: string): string => {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return '';
  const rgb = (h: string) => [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  const [r, g, b] = rgb(m[1]);
  let best = NAMED_COLOURS[0]; let bestDist = Infinity;
  NAMED_COLOURS.forEach((entry) => {
    const [nr, ng, nb] = rgb(entry[1].slice(1));
    const d = (r - nr) ** 2 + (g - ng) ** 2 + (b - nb) ** 2;
    if (d < bestDist) { bestDist = d; best = entry; }
  });
  const preset = BRAND_PRESETS.find((p) => p.value.toLowerCase() === `#${m[1]}`.toLowerCase());
  return preset ? preset.label : best[0];
};

/**
 * A thumbnail of each navigation layout: a top bar, then where the menu sits
 * and where the content goes. Deliberately crude so the eye compares shape.
 */
const layoutThumb = (layout: NavLayout, t: Tokens, accent: string) => {
  const bar = <div style={{ height: '9px', backgroundColor: accent, borderRadius: '2px 2px 0 0' }} />;
  const block = (style: React.CSSProperties = {}) => (
    <div style={{ backgroundColor: t.bgMuted, borderRadius: '2px', ...style }} />
  );
  const body: React.CSSProperties = { flex: 1, display: 'flex', gap: '4px', padding: '4px' };
  return (
    <div style={{
      width: '118px', height: '72px', display: 'flex', flexDirection: 'column',
      backgroundColor: t.bgMain, border: `1px solid ${t.border}`, borderRadius: '4px', overflow: 'hidden'
    }}>
      {bar}
      {layout === 'topmenu' && (
        <div style={{ height: '6px', borderBottom: `1px solid ${t.border}`, display: 'flex', gap: '3px', padding: '1px 4px' }}>
          {[14, 18, 12, 16].map((w, i) => <div key={i} style={{ width: `${w}px`, backgroundColor: t.primaryBorder, borderRadius: '1px' }} />)}
        </div>
      )}
      <div style={body}>
        {layout === 'sidebar' && block({ width: '26px', backgroundColor: t.primarySoft, border: `1px solid ${t.primaryBorder}` })}
        {layout === 'tiles' ? (
          <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '3px' }}>
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} style={{ backgroundColor: t.bgMuted, borderRadius: '2px' }} />
            ))}
          </div>
        ) : block({ flex: 1 })}
      </div>
    </div>
  );
};

/** A hex the colour input and the token helpers can both work with. */
const isHex = (v: string) => /^#[0-9A-Fa-f]{6}$/.test(v);

const AppearanceSection = ({ form, setForm, saving, onSubmit, t }: SettingsSectionProps) => {
  // One look for every pick-one row on this section.
  const choiceStyle = (selected: boolean, accent = t.primary): CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '8px 14px', minHeight: '40px', cursor: 'pointer',
    borderRadius: t.radius.pill,
    border: `2px solid ${selected ? accent : t.border}`,
    backgroundColor: selected ? t.bgHover : t.bgMain,
    color: t.textMain, fontWeight: selected ? 'bold' : 'normal',
    fontSize: t.fontSize.md
  });

  return (
    <FormLayout
      title="Appearance"
      submitLabel="💾 Save Appearance"
      submitting={saving}
      onSubmit={onSubmit}
    >
      <FormLayout.Section title="Navigation Layout" />
      <p style={{ color: t.textMuted, fontSize: t.fontSize.md, margin: '0 0 14px' }}>
        How everyone moves between modules. Every module works the same under
        each; only the menu changes. Applies the moment you save.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
        {NAV_LAYOUTS.map((option) => {
          const selected = (form.navigationLayout || DEFAULT_NAV_LAYOUT) === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => setForm({ ...form, navigationLayout: option.value })}
              aria-pressed={selected}
              style={{
                display: 'flex', gap: '14px', alignItems: 'flex-start', textAlign: 'left',
                flex: '1 1 280px', maxWidth: '360px', padding: '12px 14px', cursor: 'pointer',
                borderRadius: t.radius.lg,
                border: `2px solid ${selected ? t.primary : t.border}`,
                backgroundColor: selected ? t.primarySoft : t.bgMain, color: t.textMain
              }}
            >
              {layoutThumb(option.value, t, form.themeColor || brand.primary)}
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontWeight: 'bold', fontSize: t.fontSize.md }}>{option.label}</span>
                <span style={{ display: 'block', color: t.textMuted, fontSize: t.fontSize.sm, marginTop: '4px' }}>{option.hint}</span>
              </span>
            </button>
          );
        })}
      </div>

      <FormLayout.Section title="Brand Colour" />
      <p style={{ color: t.textMuted, fontSize: t.fontSize.md, margin: '0 0 14px' }}>
        Applied across buttons, tabs, table headers, badges and printed documents.
        Modules with a colour of their own — Attendance, Assets, Pastor Turnover — keep theirs.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
        {BRAND_PRESETS.map((preset) => {
          const selected = (form.themeColor || '') === preset.value;
          return (
            <button
              key={preset.label}
              type="button"
              onClick={() => setForm({ ...form, themeColor: preset.value })}
              aria-pressed={selected}
              style={choiceStyle(selected, preset.swatch)}
            >
              <span style={{
                width: '18px', height: '18px', borderRadius: '50%',
                backgroundColor: preset.swatch, border: `1px solid ${t.border}`
              }} />
              {preset.label}
            </button>
          );
        })}
      </div>

      <FormLayout.Grid>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
          <Input
            label="Custom Colour (hex)" placeholder="#6B4277"
            value={form.themeColor} containerStyle={{ flex: 1 }}
            onChange={(e) => setForm({ ...form, themeColor: e.target.value })}
            hint={isHex(form.themeColor || '')
              ? `Looks like: ${colourName(form.themeColor || '')}`
              : form.themeColor ? 'Not a valid hex colour — e.g. #1565C0' : 'Leave blank for Liturgical Purple'}
          />
          <input
            type="color"
            aria-label="Pick a brand colour"
            value={isHex(form.themeColor || '') ? form.themeColor : brand.primary}
            onChange={(e) => setForm({ ...form, themeColor: e.target.value })}
            style={{
              width: '48px', height: '44px', padding: '2px', cursor: 'pointer',
              border: `1px solid ${t.border}`, borderRadius: t.radius.md,
              backgroundColor: t.bgMain
            }}
          />
        </div>
      </FormLayout.Grid>

      <FormLayout.Section title="Default Theme" />
      <p style={{ color: t.textMuted, fontSize: t.fontSize.md, margin: '0 0 14px' }}>
        The colour scheme the app opens in. Anyone can still switch with the
        ☀️ / 🌙 button in the top bar, and a browser that has switched keeps its own choice.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
        {THEME_CHOICES.map((choice) => {
          const selected = (form.defaultTheme || 'light') === choice.value;
          return (
            <button
              key={choice.value}
              type="button"
              onClick={() => setForm({ ...form, defaultTheme: choice.value })}
              aria-pressed={selected}
              style={choiceStyle(selected)}
            >
              {choice.label}
            </button>
          );
        })}
      </div>

      <FormLayout.Section title="Display Size" />
      <p style={{ color: t.textMuted, fontSize: t.fontSize.md, margin: '0 0 14px' }}>
        Scales every screen up or down for everyone. Printed documents are not affected.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
        {UI_SCALES.map((scale) => {
          const selected = (form.uiScale || 'normal') === scale.value;
          return (
            <button
              key={scale.value}
              type="button"
              onClick={() => setForm({ ...form, uiScale: scale.value })}
              aria-pressed={selected}
              style={choiceStyle(selected)}
            >
              {scale.label}
              <span style={{ color: t.textMuted, fontWeight: 'normal', fontSize: t.fontSize.sm }}>
                {scale.hint}
              </span>
            </button>
          );
        })}
      </div>
    </FormLayout>
  );
};

export default AppearanceSection;
